/**
 * The permission registry: every permission the system knows, grouped for the role
 * editor. It is the single source of truth — the API refuses a grant whose key is
 * not listed here, and the console renders its matrix from the same list.
 *
 * Adding a permission:
 *  1. add it to its group below
 *  2. check it in the API with `requirePermission('group.action')`
 *  3. decide whether the default roles in `default-roles.ts` should hold it
 *
 * A `scoped` permission is granted at a scope (own → tenant). An unscoped one is a
 * plain switch, stored at `tenant` scope.
 */
export interface PermissionDefinition {
  key: string;
  label: string;
  scoped: boolean;
}

export interface PermissionGroup {
  key: string;
  label: string;
  icon: string;
  permissions: readonly PermissionDefinition[];
}

const scoped = <K extends string>(key: K, label: string) => ({ key, label, scoped: true }) as const;
const flag = <K extends string>(key: K, label: string) => ({ key, label, scoped: false }) as const;

export const PERMISSION_GROUPS = [
  {
    key: 'organization',
    label: 'Organizasyon',
    icon: 'building',
    permissions: [
      flag('tenant.settings', 'Acente ayarlarını yönetme'),
      flag('companies.manage', 'Şirketleri yönetme'),
      flag('departments.manage', 'Birimleri yönetme'),
      flag('members.view', 'Ekip listesini görme'),
      flag('members.manage', 'Üyeleri yönetme'),
      flag('invitations.manage', 'Davet gönderme ve iptal etme'),
      flag('roles.manage', 'Rolleri ve yetkileri yönetme'),
      flag('audit.view', 'Denetim kaydını görme'),
      flag('billing.view', 'Abonelik ve ödemeleri görme'),
    ],
  },
  {
    key: 'customers',
    label: 'Müşteriler',
    icon: 'contact',
    permissions: [
      flag('customers.view', 'Firma ve kişileri görme'),
      flag('customers.manage', 'Firma ve kişi ekleme, düzenleme'),
      flag('customers.archive', 'Firma ve kişileri arşivleme'),
      flag('customers.identity', 'TC kimlik, pasaport ve vize numaralarını görme'),
      flag('customers.documents', 'Kişi evraklarını görme, yükleme ve silme'),
      flag('customers.purge_documents', 'Silinen evrakları beklemeden kalıcı olarak silme'),
    ],
  },
  {
    key: 'sales',
    label: 'Satış',
    icon: 'ticket',
    permissions: [
      scoped('sales.view', 'Satışları görme'),
      flag('sales.create', 'Satış oluşturma'),
      scoped('sales.edit', 'Satış düzenleme'),
      scoped('sales.delete', 'Satışı çöpe taşıma'),
      flag('sales.restore', 'Çöpten geri yükleme'),
      flag('sales.purge', 'Kalıcı silme'),
      flag('sales.merge', 'Satış birleştirme'),
      flag('sales.invoice', 'Faturalandı olarak işaretleme'),
      flag('sales.bizimhesap', 'BizimHesap fatura kesimi'),
    ],
  },
  {
    key: 'services',
    label: 'Hizmet',
    icon: 'plane',
    permissions: [
      flag('services.create', 'Hizmet ekleme'),
      scoped('services.edit', 'Hizmet düzenleme'),
      scoped('services.delete', 'Hizmet silme'),
      flag('services.checkin', 'Check-in yapma ve geri alma'),
    ],
  },
  {
    key: 'payments',
    label: 'Ödeme',
    icon: 'wallet',
    permissions: [
      scoped('payments.view', 'Ödemeleri görme'),
      flag('payments.create', 'Ödeme ekleme'),
      scoped('payments.edit', 'Ödeme düzenleme'),
      scoped('payments.delete', 'Ödeme silme'),
    ],
  },
  {
    key: 'proposals',
    label: 'Teklif',
    icon: 'file-text',
    permissions: [
      scoped('proposals.view', 'Teklifleri görme'),
      flag('proposals.create', 'Teklif oluşturma'),
      scoped('proposals.edit', 'Teklif düzenleme'),
      scoped('proposals.delete', 'Teklif silme'),
      flag('proposals.costs', 'Gizli maliyetleri görme'),
    ],
  },
  {
    key: 'options',
    label: 'Opsiyon',
    icon: 'hourglass',
    permissions: [
      scoped('options.view', 'Opsiyonları görme'),
      flag('options.create', 'Opsiyon ekleme'),
      scoped('options.edit', 'Opsiyon düzenleme'),
      scoped('options.delete', 'Opsiyon silme'),
      flag('options.confirm', 'Opsiyonu kapatma ve onaylama'),
    ],
  },
  {
    key: 'visa',
    label: 'Vize',
    icon: 'stamp',
    permissions: [
      scoped('visa.view', 'Vize başvurularını görme'),
      flag('visa.create', 'Vize başvurusu oluşturma'),
      scoped('visa.edit', 'Vize başvurusu düzenleme'),
      scoped('visa.delete', 'Vize başvurusu silme'),
      flag('visa.documents', 'Belge şablonlarını yönetme'),
    ],
  },
  {
    key: 'tasks',
    label: 'Görev',
    icon: 'check-square',
    permissions: [
      scoped('tasks.view', 'Görevleri görme'),
      flag('tasks.create', 'Görev oluşturma'),
      flag('tasks.assign', 'Görev atama ve devretme'),
      scoped('tasks.delete', 'Görev silme'),
      flag('tasks.rules', 'Otomatik görev kurallarını yönetme'),
    ],
  },
  {
    key: 'records',
    label: 'Kayıtlar',
    icon: 'database',
    permissions: [
      flag('suppliers.view', 'Tedarikçileri ve onlara olan borçları görme'),
      flag('suppliers.manage', 'Tedarikçi ekleme, düzenleme ve arşivleme'),
      flag('cards.manage', 'Kredi kartlarını yönetme'),
      flag('tour_packages.manage', 'Tur paketlerini yönetme'),
    ],
  },
  {
    key: 'reports',
    label: 'Rapor',
    icon: 'chart',
    permissions: [scoped('reports.view', 'Raporları görme')],
  },
] as const satisfies readonly PermissionGroup[];

type GroupPermissions = (typeof PERMISSION_GROUPS)[number]['permissions'][number];

/** Every permission key, as a type. A typo in `requirePermission('...')` fails to compile. */
export type PermissionKey = GroupPermissions['key'];

const DEFINITIONS = new Map<string, PermissionDefinition>(
  PERMISSION_GROUPS.flatMap((group) =>
    group.permissions.map((p): [string, PermissionDefinition] => [p.key, p]),
  ),
);

export const PERMISSION_KEYS = [...DEFINITIONS.keys()] as PermissionKey[];

export function isPermissionKey(key: string): key is PermissionKey {
  return DEFINITIONS.has(key);
}

export function permissionDefinition(key: string): PermissionDefinition | undefined {
  return DEFINITIONS.get(key);
}
